'use client';

import { type Post } from '@shared/all/domains/posts';
import { type Upload } from '@shared/all/domains/uploads';
import { Image as ImageIcon } from 'lucide-react';
import Image from 'next/image';
import React, { type RefObject, useRef, useState } from 'react';

import useOnScreen from '../../hooks/useOnScreen';
import { getDisplaySrc, getUploadSrc } from '../../utils/uploads';
import ViewPostMedia from '../posts/ViewPostMedia';
import VideoPlayer from '../UI/Video';
import DocumentItem from './DocumentItem';

type Props = {
  data: Upload[] | undefined;
  userBlock?: boolean;
};
const Media = ({ data, userBlock }: Props) => {
  const videoRef: RefObject<HTMLVideoElement | null> = useRef(null);
  const { isInMiddle: videoIsInMiddle } = useOnScreen(videoRef);
  const [viewPostMedia, setViewPostMedia] = useState<null | {
    uploads: Upload[];
    index: number;
  }>(null);
  const renderAttachments = (uploads: Post['uploads']) => {
    if (!uploads) return null;
    if (uploads.length === 1) {
      const x = uploads[0];
      return (
        <div className="relative mb-4">
          {x.type === 'IMAGE' && (
            <Image
              width={x.display?.width ?? 800}
              height={x.display?.height ?? 600}
              sizes="100vw"
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '360px',
                objectFit: 'cover'
              }}
              src={getDisplaySrc(x)}
              alt={x.name ?? ''}
            />
          )}
          {x.type === 'VIDEO' && (
            <VideoPlayer
              ref={videoRef}
              src={getUploadSrc(x)}
              preload="metadata"
              autoPlay={videoIsInMiddle}
              loop={videoIsInMiddle}
              className="h-[360px]"
              controls={false}
            />
          )}
          {x.type === 'DOCUMENT' && <DocumentItem data={x} inline />}
        </div>
      );
    } else if (uploads.length > 1) {
      return (
        <div className="grid grid-cols-2 gap-2">
          {uploads.slice(0, 4).map((x, i) => (
            <div
              key={x.id}
              className="relative"
              onClick={(e) => {
                e.stopPropagation();
                setViewPostMedia({ uploads, index: i });
              }}
            >
              {x.type === 'IMAGE' && (
                <Image
                  width={x.display?.width ?? 800}
                  height={x.display?.height ?? 600}
                  sizes="100vw"
                  style={{
                    width: '100%',
                    height: '280px',
                    objectFit: 'cover'
                  }}
                  src={getDisplaySrc(x)}
                  alt={x.name ?? ''}
                />
              )}
              {x.type === 'VIDEO' && (
                <VideoPlayer
                  ref={videoRef}
                  src={getUploadSrc(x)}
                  preload="metadata"
                  controls={false}
                  className="h-[280px]"
                />
              )}
              {x.type === 'DOCUMENT' && <DocumentItem data={x} inline />}
              {i === 3 && uploads.length > 4 && (
                <div className="absolute inset-0 flex cursor-pointer items-center justify-center bg-black text-white opacity-50">
                  +{uploads.length - 4}
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }
  };
  return (
    <div className="flex flex-col space-y-2 rounded-lg bg-background-secondary p-3 md:items-center md:justify-center md:space-y-0 md:p-0">
      <div className="block md:hidden laptop:block lg:block">
        {data && renderAttachments(data)}
        {viewPostMedia !== null && !userBlock && (
          <ViewPostMedia
            closeHandler={() => {
              setViewPostMedia(null);
              document.body.classList.remove('removeScroller');
            }}
            data={viewPostMedia.uploads}
            initialIndex={viewPostMedia.index}
            nsfw={false}
          />
        )}
      </div>
      {data && (
        <div
          className="hidden md:block laptop:hidden lg:hidden"
          onClick={(e) => {
            e.stopPropagation();
            setViewPostMedia({
              uploads: data,
              index: 0
            });
          }}
        >
          <ImageIcon width={25} height={25} />
        </div>
      )}
      {data?.length === 0 && (
        <p className="p-2 text-center text-textSecondary">Nothing to show</p>
      )}
    </div>
  );
};

export default Media;
